import React, { ReactNode, useCallback, useContext, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { FileStateless } from '@atlassian/bitkit-file';

import { DiffStatusIcon } from 'src/components/diff-file-status/diff-file-status-icon-mapper';
import PreviewMergeDiffIcon from 'src/components/diff-file-status/preview-merge-diff-icon';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import GenericMessage from 'src/components/generic-message';
import { TOGGLE_DIFF_EXPANSION } from 'src/redux/pull-request/actions';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';
import { BaseDiffActionsMenu as DiffActionsMenu } from 'src/sections/repository/sections/pull-request/components/diff-actions-menu/diff-actions-menu';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';
import { FileLevelAddCommentButton } from 'src/sections/repository/sections/pull-request/components/file-level-comment/file-level-add-comment-button';
import { ReviewedCheckbox } from 'src/sections/repository/sections/pull-request/components/file-reviewed-checkbox/reviewed-checkbox';
import { useSingleDiffReviewed } from 'src/sections/repository/sections/pull-request/components/reviewed-files/reviewed-files-provider';
import { PullRequestFileConversation } from 'src/sections/repository/sections/pull-request/containers/conversation/pull-request-file-conversation';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { Diff } from 'src/types/pull-request';
import {
  extractFilepath,
  extractPrevFilepath,
} from 'src/utils/extract-file-path';

import { DiffConflictLozenge } from '../diff-conflict-lozenge';
import { useFileViewed } from '../hooks/file-viewed';

import * as styles from './hidden-diff.style';

type HiddenDiffProps = {
  file: Diff;
  heading: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  isOpen?: boolean;
  diffMetadataProps: DiffMetadataProps;
};

/**
 * Common elements of different types of diffs abstracted into one component.
 * Sometimes this is really a "Hidden" diff, like when we are filtering for only
 * files with comments on them.
 * However, this component also gets used in plenty of situations where it isn't "Hidden".
 * Multiple types of diffs that are visible inherit from this like the
 * image, binary, and empty diff etc.
 */
export function HiddenDiff({
  file,
  heading,
  description,
  actions,
  isOpen = true,
  diffMetadataProps,
}: HiddenDiffProps): JSX.Element {
  const dispatch = useDispatch();
  const [isDiffActionsMenuOpen, setDiffActionsMenuOpen] = useState(false);
  const { hasMarkFilesAsViewed, hasComments } = useContext(DiffFeaturesContext);

  const isCurrentUserAnonymous = useSelector(getCurrentUserIsAnonymous);
  const { conflictMessage, fileDiffStatus, isConflicted } = file;
  const filepath = extractFilepath(file);

  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  const isReviewed = useSingleDiffReviewed(filepath);

  const onFileViewed = useFileViewed();
  const onReviewedChanged = useCallback(
    () => onFileViewed(filepath, isReviewed, file.index),
    [filepath, isReviewed, file.index, onFileViewed]
  );

  const onExpand = (filePath: string, isOpening: boolean) =>
    dispatch({
      type: TOGGLE_DIFF_EXPANSION,
      payload: { filepath: filePath, isOpening },
    });

  const dropdownMenu = (
    <DiffActionsMenu
      diffMetadataProps={diffMetadataProps}
      filepath={filepath}
      onDiffActionsMenuClicked={e => setDiffActionsMenuOpen(e.isOpen)}
      isCollapsible={!isSingleFileModeActive}
    />
  );

  const getDiffStatusIcon = () => {
    return fileDiffStatus ? (
      <DiffStatusIcon fileDiffStatus={fileDiffStatus} />
    ) : undefined;
  };

  const shouldRenderConflictLozenge = () => {
    return isConflicted && conflictMessage
      ? () => <DiffConflictLozenge conflictMessage={conflictMessage} />
      : undefined;
  };

  return (
    <FileStateless
      dropdownMenu={dropdownMenu}
      icon={getDiffStatusIcon()}
      isCollapsible={!isSingleFileModeActive}
      isDropdownMenuOpen={isDiffActionsMenuOpen}
      isExpanded={isOpen}
      isReviewed={hasMarkFilesAsViewed ? isReviewed : undefined}
      toggleExpanded={() => onExpand(filepath, !isOpen)}
      filePath={filepath}
      prevFilePath={extractPrevFilepath(file)}
      renderAfterFilePath={shouldRenderConflictLozenge()}
      previewMergeDiffIcon={<PreviewMergeDiffIcon />}
      hasStickyHeader
      renderBeforeActions={() => (
        <>
          {hasMarkFilesAsViewed ? (
            <ReviewedCheckbox
              filePath={filepath}
              onChange={onReviewedChanged}
            />
          ) : null}
          {!isCurrentUserAnonymous && hasComments ? (
            <FileLevelAddCommentButton filePath={filepath} />
          ) : null}
        </>
      )}
    >
      {hasComments && <PullRequestFileConversation file={file} />}
      <GenericMessage iconType="info" title={heading}>
        {description && description}
        {actions && <styles.ActionsList>{actions}</styles.ActionsList>}
      </GenericMessage>
    </FileStateless>
  );
}
